<template>
  <v-app v-if="!accessToken" id="yywd-front">
    <head>
      <title>{{ pageTitle }}</title>
    </head>
    <v-main>
      <v-container class="fill-height" fluid>
        <v-row align="center" justify="center" class="my-16">
          <v-col cols="12" sm="8" md="4">
            <v-img
              :src="
                logo_url
                  ? logo_url
                  : require('./assets/' + $LogoFolder + $t('$assets.fullLogo'))
              "
              contain
            />
          </v-col>
          <v-col v-if="showLoginDialog" cols="12" sm="8" md="4">
            <v-card class="elevation-12">
              <v-toolbar color="primary" dark flat>
                <v-toolbar-title>
                  <span>
                    <v-img
                      :src="
                        require('./assets/' +
                          $LogoFolder +
                          $t('$assets.logoSmallWhite'))
                      "
                      contain
                      height="32"
                      width="32"
                      class="me-3"
                      :class="$vuetify.rtl ? 'float-right' : 'float-left'"
                    />
                    {{ appTitle }}
                  </span>
                </v-toolbar-title>
              </v-toolbar>
              <v-card-text>
                <ValidationObserver ref="observer">
                  <v-form>
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('App.account')"
                      rules="required"
                    >
                      <v-text-field
                        name="account_id"
                        prepend-icon="fa-tag"
                        type="text"
                        id="account_id"
                        v-model="login.account_id"
                        :label="$t('App.account')"
                        :error-messages="login.accountErrors.concat(errors)"
                        :disabled="loading"
                        @keyup.enter="doLogin()"
                    /></ValidationProvider>
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('App.username')"
                      rules="required"
                    >
                      <v-text-field
                        name="account_user_id"
                        prepend-icon="fa-user"
                        type="text"
                        id="account_user_id"
                        v-model="login.account_user_id"
                        :label="$t('App.username')"
                        :error-messages="login.usernameErrors.concat(errors)"
                        :disabled="loading"
                        @keyup.enter="doLogin()"
                    /></ValidationProvider>
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('App.password')"
                      rules="required"
                    >
                      <v-text-field
                        name="password"
                        prepend-icon="fa-lock"
                        id="password"
                        v-model="login.password"
                        :label="$t('App.password')"
                        :error-messages="login.passwordErrors.concat(errors)"
                        :disabled="loading"
                        @keyup.enter="doLogin()"
                        :append-icon="showPassword ? 'fa-eye' : 'fa-eye-slash'"
                        :type="showPassword ? 'text' : 'password'"
                        @click:append="showPassword = !showPassword"
                    /></ValidationProvider>
                  </v-form>
                </ValidationObserver>
              </v-card-text>
              <v-card-actions class="px-4">
                <v-checkbox dense v-model="remember" class="mt-n10">
                  <template v-slot:label>
                    <span class="caption">{{ $t("App.Remember") }}</span>
                  </template>
                </v-checkbox>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click.stop="doLogin()">{{
                  $t("App.loginBtn")
                }}</v-btn>
              </v-card-actions>
              <v-progress-linear rounded :indeterminate="loading" />
            </v-card>
          </v-col>
        </v-row>
      </v-container>
      <v-footer v-if="showLoginDialog" inset app absolute color="transparent">
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="4"></v-col>
          <v-col
            cols="12"
            sm="8"
            md="4"
            :align="$vuetify.rtl ? 'left' : 'right'"
          >
            <v-menu open-on-hover top offset-y class="text-center">
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined v-bind="attrs" v-on="on">{{
                  $t("langTitle")
                }}</v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="lang in langs"
                  :key="lang.key"
                  @click="locale = lang.key"
                >
                  <v-list-item-title>{{ lang.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <span class="ms-3">
              <v-btn
                icon
                v-if="!$vuetify.theme.dark"
                @click.stop="toggleDark()"
              >
                <v-icon>fa-sun</v-icon>
              </v-btn>
              <v-btn icon v-else @click.stop="toggleDark()">
                <v-icon>fa-moon</v-icon>
              </v-btn>
            </span>
          </v-col>
        </v-row>
      </v-footer>
    </v-main>

    <Footer />
  </v-app>
  <v-app v-else-if="accessToken && !dashBoardMode" id="yywd-front">
    <head>
      <title>
        {{ navName ? navName + " - " + pageTitle : pageTitle }}
      </title>
    </head>
    <v-navigation-drawer
      v-if="!printMode && !fullscreenMode"
      class="hidden-print"
      v-model="primaryDrawer.model"
      :clipped="primaryDrawer.clipped"
      :mini-variant="primaryDrawer.mini"
      app
      overflow
      dark
      overlay-opacity="0"
      color="sidebar"
      :right="$vuetify.rtl"
    >
      <Nav
        :settingMode="settingMode"
        :login="login"
        :locale="locale"
        @backToHome="
          settingMode = false;
          $route.path != '/' ? $router.push('/') : null;
        "
      />

      <template v-slot:append>
        <v-text-field
          v-if="$store.state.searchDistrict"
          class="mx-4"
          v-model="districts_search"
          :label="$t('District.search')"
          flat
          hide-details
          clearable
        ></v-text-field>
        <v-sheet
          class="transparent-background-color overflow-y-auto"
          style="max-height: calc(65vh - 180px)"
        >
          <v-treeview
            v-if="$store.state.searchDistrict"
            v-model="tree"
            :items="districts"
            :search="districts_search"
            :filter="districts_filter"
            :open.sync="districts_open"
            :active.sync="districts_active"
            item-key="id"
            item-children="districts"
            dense
            shaped
            hoverable
            activatable
            return-object
          />
        </v-sheet>
        <v-divider />
        <v-container class="d-none">
          <v-row class="px-4 py-2">
            <v-menu dark open-on-hover top offset-y class="text-center">
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined v-bind="attrs" v-on="on">{{
                  $t("langTitle")
                }}</v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="lang in langs"
                  :key="lang.key"
                  @click="locale = lang.key"
                >
                  <v-list-item-title>{{ lang.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-spacer />
            <v-btn icon v-if="!$vuetify.theme.dark" @click.stop="toggleDark()">
              <v-icon>fa-sun</v-icon>
            </v-btn>
            <v-btn icon v-else @click.stop="toggleDark()">
              <v-icon>fa-moon</v-icon>
            </v-btn>
          </v-row>
        </v-container>
      </template>
    </v-navigation-drawer>

    <v-app-bar
      v-if="!printMode && !fullscreenMode"
      class="hidden-print"
      :clipped-left="primaryDrawer.clipped"
      :clipped-right="primaryDrawer.clipped"
      app
      dense
      flat
      color="topbar"
    >
      <v-app-bar-nav-icon
        @click.stop="primaryDrawer.model = !primaryDrawer.model"
        color="navIcon"
      ></v-app-bar-nav-icon>
      <v-toolbar-title>
        <v-img
          v-if="!logo_url"
          :class="primaryDrawer.model ? 'd-flex d-lg-none' : ''"
          style="width: 100px; height: 48px"
          :src="require('./assets/' + $LogoFolder + $t('$assets.fullLogo'))"
          contain
        />
      </v-toolbar-title>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            small
            dark
            target="Dashboard"
            href="/#/dashboard"
            link
            color="primary"
            class="px-2 me-2 mb-1"
          >
            <v-icon x-small class="pe-1">fa-table</v-icon>
            <span style="line-height: 28px">
              {{ $t("Dashboard.btn") }}
            </span>
          </v-btn>
        </template>
        <span>{{ $t("Nav.dashboard") }}</span>
      </v-tooltip>
      <v-spacer></v-spacer>
      <span
        class="me-6 d-none d-md-flex subtitle-2 secondary--text"
        v-if="!searchRoutesFlg"
      >
        {{
          $t("welcomeMessage", [
            accountUser ? accountUser.name : "",
            accountInfo ? accountInfo.account_name : "",
          ])
        }}
      </span>
      <SearcherRoutes
        v-if="searchRoutesFlg"
        @selected="searchRoutesFlg = !searchRoutesFlg"
      />
      <v-tooltip bottom v-if="!settingMode">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            color="navIcon"
            @click.stop="searchRoutesFlg = !searchRoutesFlg"
          >
            <v-icon small>fa-search</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Search.searchUse") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="!settingMode">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            color="navIcon"
            @click.stop="$router.push('/my-self')"
          >
            <v-icon small>fa-user</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("App.myAccount") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="!settingMode">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            color="navIcon"
            @click.stop="$router.push('/account-qacenter')"
          >
            <v-icon small>fa-pen-square</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("App.accountQacenter") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="!settingMode">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            color="navIcon"
            @click.stop="settingMode = !settingMode"
          >
            <v-icon small>fa-cogs</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("App.settings") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="!settingMode">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            color="navIcon"
            @click.stop="$router.push('/notification')"
          >
            <v-badge
              :color="unReadNotice > 0 ? 'accent' : 'transparent'"
              :content="unReadNotice"
              offset-y="15"
              offset-x="15"
              overlap
            >
              <v-icon small>fa-bell</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <span>{{ $t("App.notification") }}</span>
      </v-tooltip>
      <span v-else>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              color="navIcon"
              @click.stop="searchRoutesFlg = !searchRoutesFlg"
            >
              <v-icon small>fa-search</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Search.search") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              color="navIcon"
              @click.stop="settingMode = !settingMode"
            >
              <v-icon small>fa-home</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("App.home") }}</span>
        </v-tooltip>
      </span>

      <v-tooltip bottom v-if="settings && settings.shop_psi_add_cost_type">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" color="navIcon" href="#/alerts">
            <v-badge
              :color="toreads > 0 ? 'accent' : 'transparent'"
              :label="toreads > 999 ? '999+' : '' + toreads"
              :content="toreads > 0 ? (toreads > 999 ? '999+' : toreads) : ''"
              offset-y="10"
              offset-x="2"
              class="my-5"
            >
              <v-icon small>fa-exclamation</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <span>{{ $t("Alert.objName") }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="me-2"
            @click.stop="doLogout()"
            color="navIcon"
          >
            <v-icon small>fa-sign-out-alt</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("App.logout") }}</span>
      </v-tooltip>
      <v-divider vertical />
      <v-btn icon v-if="!$vuetify.theme.dark" @click.stop="toggleDark()">
        <v-icon small>fa-sun</v-icon>
      </v-btn>
      <v-btn icon v-else @click.stop="toggleDark()">
        <v-icon small>fa-moon</v-icon>
      </v-btn>
    </v-app-bar>

    <Polling />

    <v-main id="app-main">
      <v-progress-linear
        v-if="loading"
        :active="loading"
        :indeterminate="loading"
        absolute
        top
        color="primary"
      />
      <v-row
        no-gutters
        dense
        v-if="printMode"
        class="py-4 px-8 hidden-print"
        style="
          position: absolute;
          top: 0px;
          left: 0px;
          right: 0px;
          background-color: rgba(255, 255, 255, 0.6) !important;
          z-index: 2 !important;
        "
      >
        <v-col :cols="getPrintCols">
          <v-select
            v-model="printPage"
            :items="$t('$print.pageNames')"
            dense
            solo
            hide-details
          />
        </v-col>
        <v-col :cols="getPrintCols" class="ml-5">
          <v-select
            v-model="printFont"
            :items="$t('$print.fontSizeNames')"
            dense
            solo
            hide-details
          />
        </v-col>
        <v-spacer />
        <v-btn dark color="grey" class="me-2 px-6" @click.stop="$router.go(-1)">
          <v-icon x-small class="pe-1">fa-reply</v-icon>
          {{ $t("CRUD.back") }}
        </v-btn>
        <v-btn dark color="print" class="px-6" v-if="!printPreviewMode" v-print>
          <v-icon x-small class="pe-1">fa-print</v-icon>
          {{ $t("Print.print") }}
        </v-btn>
      </v-row>
      <v-sheet
        id="main-content"
        :color="printMode || fullscreenMode ? '' : 'topbar'"
        :class="
          printMode
            ? 'app-overflow-y-scroll app-fill-height'
            : 'app-overflow-y-scroll app-fill-content-height'
        "
        @scroll="scrollEvent()"
      >
        <router-view
          :style="
            printMode
              ? 'margin-left:auto;margin-right:auto;width:' +
                printPageOptions[printPageSize].width +
                'px;'
              : ''
          "
          :class="printMode ? 'print-layout-container' : ''"
          @change-district="changeDistrict"
          @to-parent-district="toParentDistrict"
        ></router-view>
        <v-data-table v-if="printMode" hidden />
      </v-sheet>
      <v-fab-transition>
        <v-btn
          v-if="toTopActive"
          color="accent"
          class="hidden-print"
          fab
          fixed
          bottom
          :left="$vuetify.rtl"
          :right="!$vuetify.rtl"
          @click.stop="toTop()"
        >
          <v-icon>fa-angle-up</v-icon>
        </v-btn>
      </v-fab-transition>
    </v-main>

    <Footer v-if="!printMode && !fullscreenMode" />

    <v-overlay
      :value="$store.state.fullscreenLoading"
      style="z-index: 999999 !important"
    >
      <v-progress-circular
        size="50"
        color="accent"
        indeterminate
      ></v-progress-circular>
    </v-overlay>
    <!--    <v-overlay-->
    <!--      opacity="0.67"-->
    <!--      v-if="$store.state.fullscreenImages"-->
    <!--      style="z-index: 999999 !important"-->
    <!--    >-->
    <!--      <v-carousel-->
    <!--        height="100%"-->
    <!--        hide-delimiters-->
    <!--        :show-arrows="-->
    <!--          $store.state.fullscreenImages &&-->
    <!--          $store.state.fullscreenImages.length > 1-->
    <!--        "-->
    <!--      >-->
    <!--        <v-carousel-item-->
    <!--          v-for="(item, i) in $store.state.fullscreenImages"-->
    <!--          :key="i"-->
    <!--          class="fullscreen-images"-->
    <!--          @click="$showFullscreenImages(null)"-->
    <!--        >-->
    <!--          <v-btn-->
    <!--            small-->
    <!--            dark-->
    <!--            color="primary"-->
    <!--            style="position: absolute;top: 95%;right: 50%;z-index:99999999"-->
    <!--            @click.stop="downloadImg(item)"-->
    <!--          >-->
    <!--            <v-icon x-small class="pe-1">fa-file-download</v-icon>-->
    <!--            {{ $t("Material.Fields.download_img") }}-->
    <!--          </v-btn>-->
    <!--          <v-img :src="item" contain height="90%" />-->
    <!--        </v-carousel-item>-->
    <!--      </v-carousel>-->
    <!--    </v-overlay>-->
  </v-app>
  <v-app v-else-if="accessToken && dashBoardMode" id="yywd-dashboard" style="background: #0A1D3C !important; color: #ccc !important;">
    <head>
      <title>
        {{  $t("Nav.dashboard") + " - " + pageTitle  }}
      </title>
    </head>
    <div class="dashboard_title">
      {{ $t("Dashboard.title") }}
    </div>
    <v-main id="app-main">
      <v-progress-linear
        v-if="loading"
        :active="loading"
        :indeterminate="loading"
        absolute
        top
        color="primary"
      />
      <v-sheet
        id="main-content"
        class="app-overflow-y-scroll app-fill-content-height v-dashboard_sheet"
      >
        <router-view></router-view>
      </v-sheet>
    </v-main>
    <v-overlay
     :value="$store.state.fullscreenLoading"
     style="z-index: 999999 !important"
    >
      <v-progress-circular
       size="50"
       color="accent"
       indeterminate
      ></v-progress-circular>
    </v-overlay>
  </v-app>
</template>

<style scoped>
.dashboard_title {
  margin: auto;
  color: #4D9FFF;
  font-size: 2.5rem;
  padding-top: 10px !important;
}
.v-dashboard_sheet {
  overflow: hidden;
  height: 100%;
  padding-top: 30px !important;
}
</style>

<script>
import Polling from "./components/Polling";
import Nav from "./Nav";
import Footer from "./Footer";
import i18n from "./i18n";
import { mapState } from "vuex";
import Auth from "@/api/authorizations";
import AccountUsers from "@/api/account_users";
import Districts from "@/api/districts";
import Cities from "@/api/cities";
import { localize, ValidationObserver, ValidationProvider } from "vee-validate";
import BaseAnnouncement from "@/api/base_announcement";
import SearcherRoutes from "@/components/plugins/SearcherRoutes";
import Settings from "@/api/settings";
import "viewerjs/dist/viewer.css";
import VueViewer from "v-viewer";
import Vue from "vue";
Vue.use(VueViewer);

if (localStorage.getItem("locale"))
  i18n.locale = localStorage.getItem("locale");

export default {
  name: "App",

  components: {
    Polling,
    Nav,
    Footer,
    ValidationObserver,
    ValidationProvider,
    SearcherRoutes,
  },

  computed: {
    from() {
      return this.$route
        ? this.$route.params
          ? this.$route.params.from
          : ""
        : "";
    },
    force_login() {
      return this.$route
        ? this.$route.params
          ? this.$route.params.force_login
          : ""
        : "";
    },
    districts_filter() {
      return (item, districts_search, textKey) =>
        item[textKey].indexOf(districts_search) > -1;
    },
    appTitle() {
      switch (this.$LogoFolder) {
        case "fengputech/":
          return "蜂铺科技";
      }
      return this.$t("App.title");
    },
    pageTitle() {
      switch (this.$LogoFolder) {
        case "fengputech/":
          return "蜂铺科技";
      }
      return this.$t("pageTitle");
    },
    getPrintCols() {
      if (window.innerWidth < 300) {
        return 6;
      } else if (window.innerWidth < 500) {
        return 5;
      } else if (window.innerWidth < 600) {
        return 4;
      } else if (window.innerWidth < 700) {
        return 3;
      }
      return 2;
    },
    accountID() {
      return this.accountInfo ? this.accountInfo.account_id : "";
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState([
      "loading",
      "showSearchPanel",
      "showSparklinePanel",
      "fixHeaderHeightInPx",
      "mainContentScrolled",
      "printPageSize",
      "printFontSize",
      "printMode",
      "fullscreenMode",
      "dashBoardMode",
      "accessToken",
      "accountInfo",
      "accountUser",
      "settings",
      "permission_districts",
      "serverDistricts",
      "districtID",
      "toreads",
      "unReadNotice",
      "fullscreenImages",
      "navName",
    ]),
  },

  data: () => ({
    logo_url: null,
    showLoginDialog: false,
    toTopActive: false,
    primaryDrawer: {
      model: true,
      type: "Default (no property)",
      clipped: false,
      mini: false,
    },
    showPassword: false,
    login: {
      account_id: "",
      account_user_id: "",
      password: "",
      accountErrors: [],
      usernameErrors: [],
      passwordErrors: [],
    },
    langs: [
      { title: "简体中文", key: "zhHans" },
      { title: "English", key: "en" },
      { title: "Español", key: "es" },
    ],
    locale: "",
    remember: true,
    tree: [],
    districts: [
      {
        id: 0,
        name: i18n.t("District.all"),
        districts: [],
      },
    ],
    districts_search: null,
    districts_open: [],
    districts_active: [{ id: 0 }],
    printPage: i18n.t("$print.defaultPageOption"),
    printPageOptions: i18n.t("$print.pageOptions"),
    printFont: i18n.t("$print.defaultFontSizeOptions"),
    printFontSizeOptions: i18n.t("$print.fontSizeOptions"),
    printPreviewMode: false,
    settingMode: false,
    materialSelected: null,
    searchRoutesFlg: false,
  }),

  watch: {
    $route(to) {
      if (to.query.base_user_id) {
        Auth.login({
          base_user_id: to.query.base_user_id,
          secret: to.query.secret,
          account_id: to.query.account_id,
          account_user_id: to.query.account_user_id,
        }); // 支持Base总部超级用户换取登录状态
        this.$router.replace("/");
      }

      this.logo_url = localStorage.getItem("logo_url");
      //判断是否是设置模块
      if (to.path.startsWith("/settings/")) {
        this.settingMode = true;
      } else {
        this.settingMode = false;
      }
      // 打印模式切换↓
      this.$store.commit("printMode", to.path.startsWith("/print/"));
      if (this.printMode) {
        var printPageSize = localStorage.getItem("printPageSize");
        printPageSize = printPageSize
          ? printPageSize
          : i18n.t("$print.defaultPageOption");
        this.$store.commit("printPageSize", printPageSize);
        localStorage.setItem("printPageSize", printPageSize);
        this.printPage = printPageSize;

        var printFontSize = localStorage.getItem("printFontSize");
        printFontSize = printFontSize
          ? printFontSize
          : i18n.t("$print.defaultFontSizeOptions");
        this.$store.commit("printFontSize", printFontSize);
        localStorage.setItem("printFontSize", printFontSize);
        this.printFont = printFontSize;
        if (this.$route.path === '/print/stock-orders-out-merge-print-preview') {
          this.printPreviewMode = true;
        } else {
          this.printPreviewMode = false;
        }
      }
      // 全屏页面模式切换↓
      this.$store.commit("fullscreenMode", to.path.startsWith("/single/"));

      // 数据看板模式
      this.$store.commit('dashBoardMode', to.path.startsWith("/dashboard"))
    },
    mainContentScrolled: {
      handler() {
        this.toTopActive = this.mainContentScrolled > 100;
        // ↓fix-header效果用
        // if (this.toTopActive) this.$store.commit("closeSearchPanel");
        // else if (
        //   this.fixHeaderHeightInPx > 100 &&
        //   this.mainContentScrolled == 0
        // )
        //   this.$store.commit("openSearchPanel");
      },
    },
    showSearchPanel: {
      handler() {
        this.$setIntervalMainResizer();
      },
    },
    showSparklinePanel: {
      handler() {
        this.$setIntervalMainResizer();
      },
    },
    printPage: {
      handler() {
        this.$store.commit("printPageSize", this.printPage);
        localStorage.setItem("printPageSize", this.printPage);
      },
    },
    printFont: {
      handler() {
        this.$store.commit("printFontSize", this.printFont);
        localStorage.setItem("printFontSize", this.printFont);
      },
    },
    printMode: {
      immediate: true,
      handler() {
        if (this.printMode) this.$vuetify.theme.dark = false;
        else {
          if (
            localStorage.getItem("dark") &&
            localStorage.getItem("dark") != "false"
          )
            this.$vuetify.theme.dark = true;
          else this.$vuetify.theme.dark = false;
        }
      },
    },
    locale: {
      handler() {
        this.$i18n.locale = this.locale;
        localize({ en: i18n.t("$validate") }); // validate语言本地化
        localStorage.setItem("locale", this.locale);
      },
    },
    from: {
      handler() {
        if (this.from) {
          Auth.login({
            from: this.from,
            access_token: this.$route.params.access_token,
          }); // 尝试从跳转参数from和accessToken换取登录状态
          this.$router.replace(
            this.$route.params.redirect ? this.$route.params.redirect : "/"
          );
        }
      },
    },
    force_login: {
      handler() {
        console.log(this.force_login);
        if (this.force_login) {
          this.login.account_id = this.$route.query.accounts;
          this.login.account_user_id = this.$route.query.account_user_id;
          this.login.password = this.$route.query.password;
          this.$router.replace("/");
          this.doLogin();
        }
      },
    },
    accessToken: {
      immediate: true,
      handler() {
        if (this.accessToken) {
          AccountUsers.my();
          Districts.tree();
          Cities.fetchMap();
        } else if (this.$route.path != "/") {
          this.$router.push("/");
        }
      },
    },
    districts_active: {
      handler() {
        if (this.districts_active.length > 0)
          this.$store.commit("districtID", this.districts_active[0].id);
      },
    },
    districtID: {
      handler() {
        this.districts_open = [{ id: 0 }]; //重置tree open
        this.openDistrictsTree(this.districts, this.districtID);
        this.districts_active = [{ id: this.districtID }];
      },
    },
    serverDistricts: {
      handler() {
        this.districts[0].districts = this.getDistrictsFiltered(
          this.serverDistricts
        );
        // this.districts_open = [{ id: 0 }];//分组搜索栏默认收起还是默认打开
      },
    },
    primaryDrawer: {
      immediate: true,
      deep: true,
      handler() {
        this.$store.commit("primaryDrawer", this.primaryDrawer.model);
        this.$stickTableResizer();
      },
    },
    accountID() {
      this.getBaseAnnouncementCount();
    },
    settings: {
      handler() {
        if (this.settings && this.settings.sub_account_enable) {
          Settings.getSubAccountEditStatus();
        }
      },
    },
    fullscreenImages: {
      handler() {
        if (this.fullscreenImages) {
          this.$viewerApi({
            images: this.fullscreenImages,
          });
        }
      },
    },
  },

  mounted() {
    this.$store.commit("districtID", 0);
    this.getBaseAnnouncementCount();
  },

  created() {
    this.locale = this.$i18n.locale;
    if (localStorage.getItem("dark") && localStorage.getItem("dark") != "false")
      this.$vuetify.theme.dark = true;
    this.login.account_id = localStorage.getItem("login.account_id");
    this.login.account_user_id = localStorage.getItem("login.account_user_id");
    if (localStorage.getItem("accessToken")) {
      Auth.check().then(
        function () {
          this.showLoginDialog = !this.accessToken;
        }.bind(this)
      ); // 检查本地accessToken的登录状态
    } else {
      this.showLoginDialog = true;
    }
  },

  methods: {
    scrollEvent() {
      this.$store.commit(
        "mainContentScrolled",
        document.getElementById("main-content").scrollTop
      );
    },
    toTop() {
      this.$scrollTop(); //返回顶部
    },
    toggleDark() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
      localStorage.setItem("dark", this.$vuetify.theme.dark);
    },
    doLogin() {
      if (this.loading) return;
      if (
        !this.login.account_id ||
        !this.login.account_user_id ||
        !this.login.password
      )
        return;
      this.login.accountErrors = [];
      this.login.usernameErrors = [];
      this.login.passwordErrors = [];
      Auth.login({
        account_id: this.login.account_id,
        account_user_id: this.login.account_user_id,
        password: this.login.password,
      }).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.showLoginDialog = false;
            this.getBaseAnnouncementCount();
            if (this.remember) {
              localStorage.setItem("login.account_id", this.login.account_id);
              localStorage.setItem(
                "login.account_user_id",
                this.login.account_user_id
              );
            } else {
              localStorage.removeItem("login.account_id");
              localStorage.removeItem("login.account_user_id");
            }
          } else if (response.data.error.code == "ERR020") {
            //品牌校验失败
            this.login.accountErrors = [response.data.error.msg];
          } else if (response.data.error.code == "ERR002") {
            //账号密码错误
            this.login.usernameErrors = [response.data.error.msg];
            this.login.passwordErrors = [response.data.error.msg];
          } else {
            //其他错误（包括ERR001登录失败）
            this.login.passwordErrors = [response.data.error.msg];
          }
        }.bind(this)
      );
    },
    doLogout() {
      Auth.logout();
      this.showLoginDialog = true;
    },
    getDistrictsFiltered(districts) {
      if (!this.permission_districts || this.permission_districts.length == 0)
        return districts;
      return districts.filter((district) => {
        district.districts = this.getDistrictsFiltered(district.districts); // 递归下级地区
        if (
          (district.districts && district.districts.length > 0) || // 有下级分类的
          this.permission_districts.find((d) => d == district.code) // 和自身有权限的
        )
          return true; // 符合筛选条件
      });
    },
    openDistrictsTree(tree, nodeId) {
      for (var i = 0; i < tree.length; i++) {
        let obj = tree[i];
        if (obj.id == nodeId) {
          // console.log("return obj" + obj.id);
          this.districts_open.push(obj);
          return obj; //找到就结束递归
        } else if (obj.districts && obj.districts.length > 0) {
          //有下层就递归往下一层找
          let node = this.openDistrictsTree(obj.districts, nodeId);
          if (node) {
            this.districts_open.push(obj);
            // console.log("return node" + node.id);
            return node;
          }
        }
      }
      // console.log("return null" + tree.id);
      return null;
    },
    findDistrictNode(tree, nodeId) {
      for (var i = 0; i < tree.length; i++) {
        let obj = tree[i];
        if (obj.id == nodeId) {
          return obj; //找到就结束递归
        } else if (obj.districts && obj.districts.length > 0) {
          //有下层就递归往下一层找
          let node = this.openDistrictsTree(obj.districts, nodeId);
          if (node) {
            return node;
          }
        }
      }
      return null;
    },
    changeDistrict(district) {
      if (district) this.$store.commit("districtID", district.id);
    },
    toParentDistrict() {
      if (this.districtID) {
        let district = this.findDistrictNode(this.districts, this.districtID);
        if (district)
          this.$store.commit(
            "districtID",
            district.parent_id ? district.parent_id : 0
          );
      }
    },
    getBaseAnnouncementCount() {
      if (!this.showLoginDialog && this.accountID != "") {
        BaseAnnouncement.getUnreadBaseAnnouncementsCount({
          account_id: this.accountID,
        });
      }
    },
    downloadImg(url) {
      window.open(url, "_self");
    },
  },

  beforeDestroy() {
    this.$store.dispatch("threadLoadingStopAll");
    this.$store.dispatch("fullscreenLoadingStop");
  },
};
</script>